import React, { useState, useEffect } from 'react';

export default function EcommerceModal({ open, onClose, node, onSave }) {
  const [formData, setFormData] = useState({
    catalogType: 'products', // 'products', 'services', 'custom'
    catalogId: '',
    message: '',
    showPrice: true,
    showDescription: true,
    maxItems: 10,
    delay: 0
  });

  useEffect(() => {
    if (open && node) {
      setFormData({
        catalogType: node.data.catalogType || 'products',
        catalogId: node.data.catalogId || '',
        message: node.data.message || '',
        showPrice: node.data.showPrice !== false,
        showDescription: node.data.showDescription !== false,
        maxItems: node.data.maxItems || 10,
        delay: node.data.delay || 0
      });
    }
  }, [open, node]);

  const handleSave = () => {
    if (onSave) {
      onSave(node.id, formData);
    }
    onClose();
  };

  if (!open) return null;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const contentStyle = {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '500px',
    maxWidth: '600px',
    maxHeight: '80vh',
    overflowY: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none'
  };

  const textareaStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '12px',
    fontSize: '14px',
    marginBottom: '12px',
    outline: 'none',
    minHeight: '80px',
    resize: 'vertical',
    fontFamily: 'inherit'
  };

  const buttonStyle = {
    background: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const secondaryButtonStyle = {
    background: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer'
  };

  const radioButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    cursor: 'pointer'
  };

  const radioCircleStyle = {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  };

  const selectedRadioStyle = {
    ...radioCircleStyle,
    border: '2px solid #007bff',
    background: '#007bff'
  };

  const checkboxStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    cursor: 'pointer'
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#495057', fontSize: '20px', fontWeight: '600' }}>
            Configure E-commerce Catalog
          </h2>
        </div>

        {/* Catalog Type Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Catalog Type
          </label>
          
          <div
            style={radioButtonStyle}
            onClick={() => setFormData(prev => ({ ...prev, catalogType: 'products' }))}
          >
            <div style={formData.catalogType === 'products' ? selectedRadioStyle : radioCircleStyle}>
              {formData.catalogType === 'products' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
            </div>
            <span style={{ fontSize: '16px' }}>🛍️</span>
            <span style={{ fontSize: '14px', color: '#495057' }}>Products Catalog</span>
          </div>
          
          <div
            style={radioButtonStyle}
            onClick={() => setFormData(prev => ({ ...prev, catalogType: 'services' }))}
          >
            <div style={formData.catalogType === 'services' ? selectedRadioStyle : radioCircleStyle}>
              {formData.catalogType === 'services' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
            </div>
            <span style={{ fontSize: '16px' }}>🔧</span>
            <span style={{ fontSize: '14px', color: '#495057' }}>Services Catalog</span>
          </div>
          
          <div
            style={radioButtonStyle}
            onClick={() => setFormData(prev => ({ ...prev, catalogType: 'custom' }))}
          >
            <div style={formData.catalogType === 'custom' ? selectedRadioStyle : radioCircleStyle}>
              {formData.catalogType === 'custom' && <div style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%' }} />}
            </div>
            <span style={{ fontSize: '16px' }}>⚙️</span>
            <span style={{ fontSize: '14px', color: '#495057' }}>Custom Catalog</span>
          </div>
        </div>

        {/* Catalog Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Select Catalog
          </label>
          <select
            value={formData.catalogId}
            onChange={e => setFormData(prev => ({ ...prev, catalogId: e.target.value }))}
            style={inputStyle}
          >
            <option value="">Select a catalog</option>
            <option value="electronics">Electronics</option>
            <option value="clothing">Clothing & Fashion</option>
            <option value="home">Home & Garden</option>
            <option value="books">Books & Media</option>
            <option value="sports">Sports & Outdoors</option>
          </select>
        </div>

        {/* Introduction Message */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Introduction Message
          </label>
          <textarea
            value={formData.message}
            onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
            placeholder="Enter a message to introduce the catalog..."
            style={textareaStyle}
          />
        </div>

        {/* Display Options */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Display Options
          </label>
          
          <div
            style={checkboxStyle}
            onClick={() => setFormData(prev => ({ ...prev, showPrice: !prev.showPrice }))}
          >
            <input
              type="checkbox"
              checked={formData.showPrice}
              onChange={() => {}}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>Show Prices</span>
          </div>
          
          <div
            style={checkboxStyle}
            onClick={() => setFormData(prev => ({ ...prev, showDescription: !prev.showDescription }))}
          >
            <input
              type="checkbox"
              checked={formData.showDescription}
              onChange={() => {}}
              style={{ width: '16px', height: '16px' }}
            />
            <span style={{ fontSize: '14px', color: '#495057' }}>Show Descriptions</span>
          </div>
        </div>

        {/* Maximum Items */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Maximum Items to Show - {formData.maxItems}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="1"
              max="20"
              value={formData.maxItems}
              onChange={e => setFormData(prev => ({ ...prev, maxItems: parseInt(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '30px' }}>
              {formData.maxItems}
            </span>
          </div>
        </div>

        {/* Delay Setting */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#495057' }}>
            Delay before showing catalog - {formData.delay} sec
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="range"
              min="0"
              max="10"
              value={formData.delay}
              onChange={e => setFormData(prev => ({ ...prev, delay: parseInt(e.target.value) }))}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '14px', color: '#6c757d', minWidth: '50px' }}>
              {formData.delay} sec
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button
            onClick={handleSave}
            style={{ ...buttonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            💾 Save
          </button>
          <button
            onClick={onClose}
            style={{ ...secondaryButtonStyle, display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            ✕ Close
          </button>
        </div>
      </div>
    </div>
  );
} 